import { getSlotCalendarDate, getSlotExceptionsForDate, isDateOpenForSlotSettings } from './slot-schedule';

/** Automatic discount configured on an open calendar exception. */
export interface DatePromotionConfig {
  isEnabled?: boolean;
  percentage?: number;
  label?: Record<string, string>;
  message?: Record<string, string>;
}

/** Discount applied to the paid cart lines of one ticket type. */
export interface DatePromotionApplication {
  ticketTypeId: string;
  date: string;
  percentage: number;
  label?: Record<string, string>;
  message?: Record<string, string>;
  amount: number;
}

/** Returns the effective promotion for a ticket type and calendar day, including template periods. */
export function getDatePromotion(ticketType: any, date: string | null): (DatePromotionConfig & { percentage: number }) | null {
  if (!date || ticketType?.isDateSlot !== true || ticketType.isFree || ticketType.isGiftCard || ticketType.isCombinedTicket ||
      !isDateOpenForSlotSettings(date, ticketType.slotSettings))
    return null;

  const exceptions = getSlotExceptionsForDate(date, ticketType.slotSettings);
  if (exceptions.some(exception => exception.isClosed || exception.isPrivatized))
    return null;
  const promotions = exceptions.map(exception => exception.promotion).filter((promotion): promotion is DatePromotionConfig => promotion?.isEnabled === true);
  if (!promotions.length || promotions.some(promotion => typeof promotion.percentage !== 'number' ||
      !Number.isFinite(promotion.percentage) || promotion.percentage < 1 || promotion.percentage > 100 ||
      promotion.percentage !== promotions[0].percentage))
    return null;
  return { ...promotions[0], percentage: promotions[0].percentage! };
}

/** Lists each effective promotion day once, resolving local overrides before template exceptions. */
export function getDatePromotionDates(ticketType: any): { date: string; promotion: DatePromotionConfig }[] {
  const exceptions = [...(ticketType?.slotSettings?.exceptions || [])];
  for (const period of ticketType?.slotSettings?.periods || [])
    exceptions.push(...(period.settings?.exceptions || []));
  const dates = new Set<string>();
  for (const exception of exceptions) {
    const date = getSlotCalendarDate(exception.date);
    if (date)
      dates.add(date);
  }
  const offers: { date: string; promotion: DatePromotionConfig }[] = [];
  for (const date of Array.from(dates).sort()) {
    const promotion = getDatePromotion(ticketType, date);
    if (promotion)
      offers.push({ date, promotion });
  }
  return offers;
}

/** Clears client discount metadata and applies date discounts to paid tickets while leaving BOGO quantities intact. */
export function applyDatePromotionToCart(cart: any[], ticketTypesById: Map<string, any>, date: string | null): DatePromotionApplication[] {
  const applications = new Map<string, DatePromotionApplication>();
  const promotions = new Map<string, ReturnType<typeof getDatePromotion>>();
  for (const item of cart) {
    delete item.datePromotionPrice;
    delete item.datePromotionPercentage;
    delete item.datePromotionDate;
    delete item.datePromotionLabel;
    if (!item.ticketTypeId || !Number.isFinite(item.price) || item.price < 0 || !Number.isInteger(item.quantity) || item.quantity < 1)
      continue;
    if (!promotions.has(item.ticketTypeId))
      promotions.set(item.ticketTypeId, getDatePromotion(ticketTypesById.get(item.ticketTypeId), date));
    const promotion = promotions.get(item.ticketTypeId);
    if (!promotion)
      continue;

    item.datePromotionPrice = Math.round((item.price * (1 - promotion.percentage / 100) + Number.EPSILON) * 100) / 100;
    item.datePromotionPercentage = promotion.percentage;
    item.datePromotionDate = date;
    item.datePromotionLabel = promotion.label || {};
    let application = applications.get(item.ticketTypeId);
    if (!application) {
      application = { ticketTypeId: item.ticketTypeId, date: date!, percentage: promotion.percentage!,
        label: promotion.label || {}, message: promotion.message || {}, amount: 0 };
      applications.set(item.ticketTypeId, application);
    }
    application.amount += (item.price - item.datePromotionPrice) * item.quantity;
  }
  return Array.from(applications.values()).map(application => ({
    ...application, amount: Math.round((application.amount + Number.EPSILON) * 100) / 100
  }));
}

/** Returns a localized date discount mention for order emails and invoices. */
export function getDatePromotionItemLabel(item: any, lang: string, formattedOriginalPrice: string): string {
  if (typeof item?.datePromotionPrice !== 'number' || typeof item?.datePromotionPercentage !== 'number')
    return '';
  const percentage = item.datePromotionPercentage;
  switch ((lang || 'en').toLowerCase()) {
    case 'fr': return `Promotion par date -${percentage} % (au lieu de ${formattedOriginalPrice})`;
    case 'de': return `Datumsrabatt -${percentage} % (statt ${formattedOriginalPrice})`;
    case 'nl': return `Datumkorting -${percentage}% (in plaats van ${formattedOriginalPrice})`;
    default: return `Date promotion -${percentage}% (instead of ${formattedOriginalPrice})`;
  }
}
